import { GetSubscriptionResponse } from '@/types/generated/public_api'

/**
 * What the Billing settings tab should say about the caller's subscription.
 *
 * - `none`      — FREE with no live Stripe sub (never subscribed, or a sub that
 *                 already ended). Nothing to manage; point at the plans page.
 * - `comped`    — operator tier override / comp: capabilities exceed the billing
 *                 plan and there is no Stripe sub behind it, so the portal has
 *                 nothing to show. `plan_label` is the only wire signal for this.
 * - `active` / `trialing` / `pastDue` / `canceling` — a live Stripe sub. Every
 *                 change (plan, interval, payment method, cancel) goes through the
 *                 Stripe Billing Portal, so `portalAvailable` is true for all four.
 */
export type SubscriptionState = 'none' | 'comped' | 'active' | 'trialing' | 'pastDue' | 'canceling'

export interface SubscriptionView {
  state: SubscriptionState
  /** True when `open_billing_portal` will succeed (live Stripe sub). */
  portalAvailable: boolean
}

export const COMPED_PLAN_LABEL = 'CUSTOM'

export function describeSubscription(sub: GetSubscriptionResponse): SubscriptionView {
  if (!sub.subscriptionActive) {
    return {
      state: sub.planLabel === COMPED_PLAN_LABEL ? 'comped' : 'none',
      portalAvailable: false,
    }
  }
  // Order matters: a trial is still a trial even if it's set to lapse, and
  // past_due needs the warning regardless of a pending cancellation.
  if (sub.status === 'trialing') return { state: 'trialing', portalAvailable: true }
  if (sub.status === 'past_due') return { state: 'pastDue', portalAvailable: true }
  if (sub.cancelAtPeriodEnd) return { state: 'canceling', portalAvailable: true }
  return { state: 'active', portalAvailable: true }
}
